import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../Firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { turkishBadWordsFilter } from '../../utils/turkishBadWordsFilter';
import './CampaignDetailPage.css';

const CampaignDetailPage = () => {
    const { providerId, campaignId } = useParams();
    const [campaign, setCampaign] = useState(null);
    const [provider, setProvider] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        address: ''
    });
    const [inputErrors, setInputErrors] = useState({
        name: '',
        phone: '',
        address: ''
    });
    const [openDropdown, setOpenDropdown] = useState({
        conditions: false,
        features: true
    });

    const toggleDropdown = (dropdown) => {
        setOpenDropdown(prev => ({
            ...prev,
            [dropdown]: !prev[dropdown]
        }));
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const providerRef = doc(db, 'providers', providerId);
                const providerSnap = await getDoc(providerRef);

                if (providerSnap.exists()) {
                    setProvider({
                        id: providerSnap.id,
                        ...providerSnap.data()
                    });
                }

                const campaignRef = doc(db, `providers/${providerId}/campaigns`, campaignId);
                const campaignSnap = await getDoc(campaignRef);

                if (campaignSnap.exists()) {
                    const data = campaignSnap.data();
                    setCampaign({
                        id: campaignSnap.id,
                        ...data
                    });
                }
            } catch (error) {
                console.error("Veri çekme hatası:", error);
                toast.error('Kampanya bilgileri yüklenirken bir hata oluştu');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [providerId, campaignId]);

    const validateField = (name, value) => {
        if (name === 'phone') {
            const phoneRegex = /^5[0-9]{2}\s?[0-9]{3}\s?[0-9]{2}\s?[0-9]{2}$/;
            if (!phoneRegex.test(value)) {
                return 'Lütfen geçerli bir telefon numarası giriniz (5XX XXX XX XX)';
            }
        } else if (name === 'name' || name === 'address') {
            if (turkishBadWordsFilter.isProfane(value)) {
                return 'Lütfen uygun bir dil kullanınız';
            }
        }
        return '';
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setInputErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    };

    const validateForm = () => {
        let isValid = true;
        const newErrors = { ...inputErrors };

        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            newErrors[key] = error;
            if (error) isValid = false;
        });

        setInputErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Lütfen formdaki hataları düzeltin');
            return;
        }

        setSubmitting(true);

        try {
            await addDoc(collection(db, 'applications'), {
                ...formData,
                campaignId: campaign.id,
                campaignTitle: campaign.title,
                providerId: provider.id,
                providerName: provider.name,
                applicationDate: serverTimestamp(),
                status: 'pending',
                processed: false,
                isClean: !turkishBadWordsFilter.isProfane(JSON.stringify(formData))
            });

            setFormData({
                name: '',
                phone: '',
                address: ''
            });

            toast.success('Başvurunuz başarıyla alındı! En kısa sürede sizinle iletişime geçilecektir.');
        } catch (error) {
            console.error('Başvuru kaydedilirken hata:', error);
            toast.error('Başvuru gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderPriceInfo = () => {
        if (campaign.pricingModel === "fixed") {
            return (
                <div className="price-display">
                    <span className="new-price">{campaign.fixedMonthlyPrice} TL/ay</span>
                    <span className="price-note">Taahhüt süresince sabit fiyat</span>
                </div>
            );
        } else if (campaign.pricingModel === "tiered" && campaign.priceTiers?.length > 0) {
            return (
                <div className="price-tiers">
                    {campaign.priceTiers.map((tier, index) => (
                        <div key={index} className="tier">
                            <span className={index === 0 ? "first-tier-price" : "tier-price"}>
                                {tier.description}: <strong>{tier.monthlyPrice} TL/ay</strong>
                            </span>
                        </div>
                    ))}
                    <div className="total-duration">
                        {campaign.totalDuration} ay taahhütlü
                    </div>
                </div>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Kampanya bilgileri yükleniyor...</p>
            </div>
        );
    }

    if (!campaign || !provider) {
        return (
            <div className="not-found-container">
                <h2>Kampanya bulunamadı</h2>
                <p>Üzgünüz, istediğiniz kampanya artık mevcut değil.</p>
            </div>
        );
    }

    const DropdownSection = ({ title, items, isOpen, toggle, isFeatures = false }) => {
        const isMobile = window.innerWidth <= 768;

        return (
            <div className="dropdown-section">
                <div className="dropdown-header" onClick={toggle}>
                    <h3>{title}</h3>
                    <span className="dropdown-icon">{isOpen ? '−' : '+'}</span>
                </div>
                <div className={`dropdown-content ${isOpen ? 'open' : ''}`}>
                    {isFeatures ? (
                        <ul className="features-list">
                            {items.map((item, index) => (
                                <li key={index} className="feature-item">
                                    <span className="campaign-feature-icon">✓</span>
                                    <span>{isMobile && item.length > 155 ? `${item.substring(0, 150)}...` : item}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <ul className="conditions-list">
                            {items.map((item, index) => (
                                <li key={index} className="condition-item">
                                    <span className="condition-icon">✓</span>
                                    <span>{isMobile && item.length > 80 ? `${item.substring(0, 77)}...` : item}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="campaign-detail-page">
            <ToastContainer position="top-right" autoClose={5000} />

            {/* Hero Section */}
            <section className="hero-section" style={{ background: provider.brandColor || '#4f46e5' }}>
                <div className="container">
                    <div className="hero-content">
                        <div className="provider-badge">
                            <img src={provider.logo} alt={provider.name} className="provider-logo" />
                            <span>{provider.name}</span>
                        </div>

                        <h1>{campaign.title}</h1>
                        <p className="campaign-description">{campaign.description}</p>

                        <div className="campaign-highlights">
                            {/* Hız Kartı */}
                            <div className="speed-badge">
                                <div className="speed-label">İNTERNET HIZI</div>
                                <div className="speed-value">{campaign.speed}</div>
                                <div className="speed-unit">Mbps</div>
                            </div>

                            {/* Fiyat Kartı */}
                            <div className="price-container">
                                {renderPriceInfo()}
                            </div>

                            {/* Ekstra Özellik Kartı (Opsiyonel) */}
                            {campaign.uploadSpeed && (
                                <div className="feature-card">
                                    <div className="campaign-feature-icon">↑</div>
                                    <div className="feature-title">YÜKLEME HIZI</div>
                                    <div className="feature-value">{campaign.uploadSpeed} Mbps</div>
                                </div>
                            )}

                            {/* Ekstra Özellik Kartı (Opsiyonel) */}
                            {campaign.quota && (
                                <div className="feature-card" style={{ background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)" }}>
                                    <div className="campaign-feature-icon">🔄</div>
                                    <div className="feature-title">KOTA</div>
                                    <div className="feature-value">{campaign.quota}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <div className="main-content">
                <div className="container">
                    <div className="content-wrapper">
                        {/* Campaign Details */}
                        <section className="campaign-details">
                            <h2 className="section-title">Kampanya Detayları</h2>

                            <DropdownSection
                                title="Kampanya Koşulları"
                                items={campaign.conditions || []}
                                isOpen={openDropdown.conditions}
                                toggle={() => toggleDropdown('conditions')}
                            />

                            {campaign.features && campaign.features.length > 0 && (
                                <DropdownSection
                                    title="Kampanya Özellikleri"
                                    items={campaign.features}
                                    isOpen={openDropdown.features}
                                    toggle={() => toggleDropdown('features')}
                                    isFeatures
                                />
                            )}
                        </section>

                        {/* Application Form */}
                        <section className="application-section">
                            <div className="application-card">
                                <div className="form-header">
                                    <h3>Hemen Başvurun</h3>
                                    <p>Formu doldurun, sizi arayalım</p>
                                </div>

                                <form onSubmit={handleSubmit} className="application-form">
                                    <div className="form-group">
                                        <label htmlFor="name">Ad Soyad</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="Adınız Soyadınız"
                                            className={inputErrors.name ? 'error' : ''}
                                        />
                                        {inputErrors.name && <span className="error-message">{inputErrors.name}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="phone">Telefon</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            placeholder="5__ ___ __ __"
                                            className={inputErrors.phone ? 'error' : ''}
                                        />
                                        {inputErrors.phone && <span className="error-message">{inputErrors.phone}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="address">Adres</label>
                                        <textarea
                                            id="address"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                            placeholder="Adres bilgileriniz"
                                            className={inputErrors.address ? 'error' : ''}
                                        />
                                        {inputErrors.address && <span className="error-message">{inputErrors.address}</span>}
                                    </div>

                                    <button type="submit" className="submit-btn" disabled={submitting}>
                                        {submitting ? (
                                            'Gönderiliyor...'
                                        ) : (
                                            <>
                                                HEMEN BAŞVUR
                                                <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
                                                    <path d="M1 7H17M17 7L11 1M17 7L11 13" stroke="currentColor" strokeWidth="2" />
                                                </svg>
                                            </>
                                        )}
                                    </button>

                                    <div className="form-note">
                                        <p>Kişisel verileriniz <a href="/kvkk">Gizlilik Politikası</a>'na uygun şekilde korunmaktadır.</p>
                                    </div>
                                </form>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CampaignDetailPage;